import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { parseResponse } from "@/lib/query-helper";
import { jobSpecialtyQueryKeys } from "@/hooks/data/v2/query-keys";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { QueryClient, useMutation, UseMutationOptions, useQuery } from "@tanstack/react-query";
import { JobSpecialty } from "@t/JobSpecialty";
import { JobSpecialtySaveDto } from "@t/JobSpecialtySaveDto";

export function useGetJobSpecialtiesByFilter(filter: SearchFilter) {
    const authFetch = useAuthFetch();

    const getJobSpecialtiesByFilter = async (): Promise<PaginatedResponse<JobSpecialty>> => {
        return await authFetch("/api/v1/job-specialties/get-by-filter", {
            method: "POST",
            body: JSON.stringify(filter),
        }).then((response) => response.json());
    };

    const getJobSpecialtiesByFilterQueryOptions = () => ({
        queryKey: jobSpecialtyQueryKeys.getByFilter(filter),
        queryFn: () => getJobSpecialtiesByFilter(),
        enabled: !!filter,
        select: (data: PaginatedResponse<JobSpecialty>) => data.data,
    });

    return useQuery(getJobSpecialtiesByFilterQueryOptions());
}

export function useCreateJobSpecialtyMutation(
    options?: UseMutationOptions<APIResponse<JobSpecialty>, Error, JobSpecialtySaveDto, unknown>,
    queryClient?: QueryClient
) {
    const authFetch = useAuthFetch();

    const mutationFn = async (
        JobSpecialty: JobSpecialtySaveDto
    ): Promise<APIResponse<JobSpecialty>> => {
        const response = await authFetch("/api/v1/job-specialities/create", {
            method: "POST",
            body: JSON.stringify(JobSpecialty),
        });
        return await parseResponse(response);
    };

    return useMutation(
        {
            ...options,
            mutationFn,
        },
        queryClient
    );
}

export function useUpdateJobSpecialtyMutation(
    options?: UseMutationOptions<APIResponse<JobSpecialty>, Error, JobSpecialtySaveDto, unknown>,
    queryClient?: QueryClient
) {
    const authFetch = useAuthFetch();

    const mutationFn = async (
        JobSpecialty: JobSpecialtySaveDto
    ): Promise<APIResponse<JobSpecialty>> => {
        const response = await authFetch("/api/v1/job-specialities/update", {
            method: "POST",
            body: JSON.stringify(JobSpecialty),
        });
        return await parseResponse(response);
    };

    return useMutation(
        {
            ...options,
            mutationFn,
        },
        queryClient
    );
}

export function useDeleteJobSpecialtyMutation(
    options?: UseMutationOptions<APIResponse<JobSpecialty>, Error, number, unknown>,
    queryClient?: QueryClient
) {
    const authFetch = useAuthFetch();

    const mutationFn = async (id: number): Promise<APIResponse<JobSpecialty>> => {
        const response = await authFetch(`/api/v1/job-specialities/${id}`, {
            method: "DELETE",
        });
        return await parseResponse(response);
    };

    return useMutation(
        {
            ...options,
            mutationFn,
        },
        queryClient
    );
}
